import { useState, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { patchAcceptRejectDecodedPlateVIN, patchDecodePlate } from 'actions/vehicles'
import {
  CreateVehicleResponsePayload,
  submitNewVehicleData,
  validateNewVehicleData,
  VehicleFormStates,
} from 'util/vehicle'
import { DrivingProfile, ProfileVehicle } from 'types/profile'
import { initialModalOptions, ModalOptions } from 'types/global'
import { useHandleCancelVehicle } from './useHandleCancelVehicle'
import { useInsurancePollingContext } from 'context/InsurancePollingContext'

export const useAddNewVehicleFormViewModel = () => {
  const profile = useSelector<any, DrivingProfile>((state) => state.profile)
  const { stopPolling } = useInsurancePollingContext()
  const dispatch = useDispatch()
  const history = useHistory()

  const [formState, setFormState] = useState<VehicleFormStates | null>(null)
  const [redirectPath, setRedirectPath] = useState<string>('')
  const [createVehicleResp, setCreateVehicleResp] = useState<null | CreateVehicleResponsePayload>(null)
  const [newVehicle, setNewVehicle] = useState<null | ProfileVehicle>(null)
  const [formData, setFormData] = useState<any>(null)
  const [formDataChanged, setFormDataChanged] = useState<boolean>(false)
  const [modalOptions, setModalOptions] = useState<ModalOptions>(initialModalOptions)

  // Shared handler for both initial submit and retry
  const handleData = useCallback(
    async (data: any, isRetry: boolean = false) => {
      validateNewVehicleData(dispatch, data, false)
      setFormState('searchingVehicle')
      setFormData(data)

      let vehicleId: number
      let lookupResponse: any

      if (isRetry) {
        // Retry flow: update existing vehicle's license plate
        if (!createVehicleResp) {
          return
        }
        lookupResponse = await patchDecodePlate(
          profile.id,
          createVehicleResp.id,
          data.license_plate,
          data.license_state
        )(dispatch)
        vehicleId = createVehicleResp.id
      } else {
        // Initial flow: create new vehicle and lookup by license plate
        const createVehicleResp = await submitNewVehicleData(dispatch, data)
        setCreateVehicleResp(createVehicleResp)
        vehicleId = createVehicleResp.id
        lookupResponse = await patchDecodePlate(
          profile.id,
          createVehicleResp.id,
          data.license_plate,
          data.license_state
        )(dispatch)
      }

      // Handle lookup failure
      if (!lookupResponse.success) {
        if (lookupResponse.status == 422) {
          setFormState('unsupportedType')
          return
        } else if (lookupResponse.status == 500) {
          setFormState('serverError')
          return
        } else {
          stopPolling('idle')
          setFormState('vehicleNotFound')
          return
        }
      }

      // Successfully found vehicle by license plate
      const newVehicleAfterUpdate = Array.isArray(lookupResponse.data)
        ? lookupResponse.data.find((v: ProfileVehicle) => v.details.id === vehicleId)
        : null
      setNewVehicle(newVehicleAfterUpdate)
      setRedirectPath(`vehicle-info/${vehicleId}`)
      setFormState('vehicleFound')
    },
    [dispatch, profile, createVehicleResp, stopPolling]
  )

  const handleSubmit = useCallback(async (data: any) => handleData(data, false), [handleData])

  const handleRetry = useCallback(async (data: any) => handleData(data, true), [handleData])

  const handleAcceptDecodedPlateVIN = useCallback(async () => {
    await patchAcceptRejectDecodedPlateVIN(profile.id, createVehicleResp?.id, 'accept')(dispatch)
    history.push(`/account/driving-info/vehicle-info/${createVehicleResp?.id}`)
  }, [profile.id, createVehicleResp?.id, dispatch, history])

  const handleCancel = useHandleCancelVehicle({
    vehicleId: createVehicleResp?.id,
    newVehicleFlow: true,
    setFormState,
  })

  return {
    formState,
    redirectPath,
    createVehicleResp,
    newVehicle,
    formData,
    formDataChanged,
    modalOptions,
    setFormState,
    setFormDataChanged,
    setModalOptions,
    handleData: handleSubmit,
    handleRetry,
    handleAcceptDecodedPlateVIN,
    handleCancel,
  }
}
